import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    ButtonGroup,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Close as CloseIcon,
    Email as EmailIcon,
    Flight as FlightIcon,
    PanTool as AssistantIcon,
    Person as MemberIcon,
    PublishedWithChanges as ReplacementIcon,
    Save as SaveIcon,
    Sync as ChangeIcon,
    Undo as UndoIcon,
    VisibilityOff as FilterIcon,
    History as HistoryIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { themeConstants, categoryItemVars } from '@/theme';
import {
    useAcceptChangeRequestMutation,
    useAddPlanResourceMutation,
    useCancelChangeRequestMutation,
    useCreateChangeRequestMutation,
    useDeletePlanResourceMutation,
    useGetUserInfoQuery,
    useGetResourcesSettingsQuery,
    useSaveResourcesSettingsMutation,
    useNotifyChangeMutation,
    useGetEventAuditQuery,
} from '@/store/apiSlice';
import type { AssignedCategory, Category } from '@/types';
import CategoryIcon from '@/components/CategoryIcon.tsx';
import { ICS } from '@/utils/ics';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';
import EventAuditModal from '@/components/modals/EventAuditModal';
import DeleteWindowExpiredDialog from '@/components/dialogs/DeleteWindowExpiredDialog';

// Helper components
interface ActionButtonProps {
    title: string;
    icon: ReactNode;
    onClick: () => void;
    color?: string;
    disabled?: boolean;
    isLoading?: boolean;
}

const ActionButton = ({ title, icon, onClick, color, disabled, isLoading }: ActionButtonProps) => (
    <Tooltip title={title} arrow>
        <span>
            <IconButton
                size="small"
                onClick={onClick}
                disabled={disabled === true || isLoading === true}
                sx={{ ...(color && { color }) }}
            >
                {isLoading ? <CircularProgress size={16} color="inherit" /> : icon}
            </IconButton>
        </span>
    </Tooltip>
);

interface CategoryIconBoxProps {
    category: Category;
    bgcolor: string;
    textColor?: string;
}

const CategoryIconBox = ({ category, bgcolor, textColor = 'white' }: CategoryIconBoxProps) => (
    <Tooltip title={category.name} arrow placement="top">
        <Stack
            sx={{
                width: 32,
                height: 32,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                flexShrink: 0,
                bgcolor,
                color: textColor,
            }}
        >
            <CategoryIcon category={category.icon} />
        </Stack>
    </Tooltip>
);

interface ContainerBoxProps {
    children: ReactNode;
    variant: 'highlighted' | 'self' | 'default' | 'edit' | 'empty';
    gap?: number;
}

const ContainerBox = ({ children, variant, gap = 3 }: ContainerBoxProps) => {
    const variantStyles = {
        highlighted: {
            borderColor: 'warning.main',
            background: categoryItemVars.highlighted,
            backdropFilter: themeConstants.effects.backdropBlur,
        },
        self: {
            borderColor: 'primary.main',
            background: categoryItemVars.self,
            backdropFilter: themeConstants.effects.backdropBlur,
        },
        default: {
            borderColor: 'divider',
            background: categoryItemVars.default,
            backdropFilter: themeConstants.effects.backdropBlur,
        },
        edit: {
            borderColor: 'divider',
            background: categoryItemVars.edit,
            backdropFilter: themeConstants.effects.backdropBlur,
        },
        empty: {
            borderColor: 'divider',
            background: categoryItemVars.empty,
            opacity: 0.7,
        },
    };

    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
                gap,
                px: 1,
                py: 0.5,
                border: 1,
                borderLeft: 3,
                borderRadius: 1,
                ...variantStyles[variant],
            }}
        >
            {children}
        </Stack>
    );
};

interface CategoryItemProps {
    category: Category;
    date: string;
    assigned?: AssignedCategory;
    user?: { id: number; name: string };
    absent: Record<number, string>;
    eventName?: string;
    eventLocation?: string;
    eventStartTime?: string;
    eventEndTime?: string;
}

const CategoryItem = ({
    category,
    date,
    assigned,
    user,
    absent,
    eventName,
    eventLocation,
    eventStartTime,
    eventEndTime,
}: CategoryItemProps) => {
    const { LL } = useI18nContext();
    const { alert } = useAppDialog();
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: resourcesSettings } = useGetResourcesSettingsQuery();
    const [saveResourcesSettings, { isLoading: isSavingFilter }] =
        useSaveResourcesSettingsMutation();

    const [addPlanResource, { isLoading: isAdding }] = useAddPlanResourceMutation();
    const [deletePlanResource, { isLoading: isDeleting }] = useDeletePlanResourceMutation();
    const [createChangeRequest, { isLoading: isCreatingRequest }] =
        useCreateChangeRequestMutation();
    const [cancelChangeRequest, { isLoading: isCancellingRequest }] =
        useCancelChangeRequestMutation();
    const [acceptChangeRequest, { isLoading: isAcceptingRequest }] =
        useAcceptChangeRequestMutation();
    const [notifyChange, { isLoading: isNotifying }] = useNotifyChangeMutation();

    // Auto-load audit data for admins to check if entries exist for this category
    const { data: auditData } = useGetEventAuditQuery({ date }, { skip: !userInfo?.admin });

    // Check if there are any audit entries for this specific category
    const hasAuditEntries = useMemo(() => {
        if (!auditData) return false;
        return auditData.some(
            (entry) =>
                entry.categoryId === category.id && entry.action && entry.action.trim() !== ''
        );
    }, [auditData, category.id]);

    const [notified, setNotified] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
    const [deleteExpiredOpen, setDeleteExpiredOpen] = useState(false);

    // In multi-assignment mode, the parent always passes the slot's user via `user`.
    // Empty slots must stay empty (no fallback), otherwise they'd all render the same first user.
    const effectiveAssignedUser = category.allowMultiple ? user : (assigned?.users?.[0] ?? user);

    // Users already assigned to this category/date (excluding the current slot user so it doesn't
    // disable itself when editing is allowed).
    const alreadyAssignedUserIds = useMemo(() => {
        const ids = (assigned?.users ?? [])
            .map((u) => u.id)
            .filter((id) => id !== effectiveAssignedUser?.id);
        return new Set<number>(ids);
    }, [assigned, effectiveAssignedUser?.id]);

    const getInitialUser = useCallback(() => {
        if (userInfo) {
            const preferredUserId =
                effectiveAssignedUser?.id ?? (userInfo.preselect ? userInfo.userId : null);

            // Check if preferred user exists in resources
            if (
                preferredUserId &&
                category.resources.some((r) => r.id === preferredUserId) &&
                // For multi-assignment empty slot: don't preselect a user already assigned in another slot.
                (!alreadyAssignedUserIds.has(preferredUserId) ||
                    effectiveAssignedUser?.id === preferredUserId)
            ) {
                return preferredUserId;
            }
        }

        // Fall back: prefer system assistants first (matching dropdown order), then first free resource.
        const firstFreeSystemAssistant = category.resources.find(
            (r) => r.system && r.assistant && !alreadyAssignedUserIds.has(r.id)
        );
        if (firstFreeSystemAssistant) return firstFreeSystemAssistant.id;

        const firstFree = category.resources.find((r) => !alreadyAssignedUserIds.has(r.id));
        return firstFree?.id ?? category.resources[0]?.id ?? 0;
    }, [effectiveAssignedUser?.id, userInfo, category.resources, alreadyAssignedUserIds]);

    const [selectedUser, setSelectedUser] = useState<number>(getInitialUser);

    // When the slot changes (different category/date or slot user changes), reset the selection
    // so each slot behaves independently.
    useEffect(() => {
        if (!effectiveAssignedUser?.id) {
            setSelectedUser(getInitialUser());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, category.id, effectiveAssignedUser?.id]);

    // Update selectedUser when preselect preference changes or resources change
    useEffect(() => {
        // Only update if there's no assignment for THIS slot
        if (!effectiveAssignedUser?.id) {
            const isValidUser = category.resources.some((r) => r.id === selectedUser);
            if (!isValidUser && category.resources.length > 0) {
                const newUser = getInitialUser();
                setSelectedUser(newUser);
            }

            // If the currently selected user is already assigned in another slot, move to a free user.
            // If there is no free user left, keep the selection as-is so the Select still renders,
            // but all options will be disabled (which communicates "no more available").
            if (alreadyAssignedUserIds.has(selectedUser)) {
                const anyFreeLeft = category.resources.some(
                    (r) => !alreadyAssignedUserIds.has(r.id)
                );
                if (anyFreeLeft) {
                    const newUser = getInitialUser();
                    if (newUser !== selectedUser) {
                        setSelectedUser(newUser);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userInfo?.preselect,
        effectiveAssignedUser?.id,
        category.resources,
        getInitialUser,
        alreadyAssignedUserIds,
        selectedUser,
    ]);

    // Don't render if userInfo is not available yet
    if (!userInfo) {
        return null;
    }

    const showAuditButton = userInfo.admin && hasAuditEntries;
    const showAdminMenu =
        userInfo.admin ||
        userInfo.categoriesResponsible?.includes(category.id) ||
        effectiveAssignedUser !== undefined;

    // Combine all loading states to show a loading overlay
    const isLoading =
        isAdding ||
        isDeleting ||
        isCreatingRequest ||
        isCancellingRequest ||
        isAcceptingRequest ||
        isNotifying;

    // Check if current user is absent
    const imAbsent = userInfo ? absent.hasOwnProperty(userInfo.userId) : false;

    const canEdit =
        (userInfo?.admin ||
            userInfo?.categoriesAssigned?.includes(category.id) ||
            userInfo?.categoriesResponsible?.includes(category.id) ||
            userInfo?.categoriesRepresents?.includes(category.id)) &&
        (!imAbsent || userInfo?.admin || userInfo?.categoriesResponsible?.includes(category.id));
    const isMyAssignment = effectiveAssignedUser?.id === userInfo?.userId;
    const isProxyFor = userInfo?.represents?.includes(effectiveAssignedUser?.id ?? 0);
    const canInteract = isMyAssignment || isProxyFor;

    // Check if user would normally be able to edit but is absent
    const wouldBeAbleToEditIfNotAbsent =
        imAbsent &&
        !userInfo?.admin &&
        !userInfo?.categoriesResponsible?.includes(category.id) &&
        (userInfo?.categoriesAssigned?.includes(category.id) ||
            userInfo?.categoriesRepresents?.includes(category.id));

    const handleAddToCalendar = () => {
        if (!effectiveAssignedUser) {
            return;
        }

        new ICS()
            .addEvent(
                `${category.name} (${effectiveAssignedUser.name})`,
                eventName && eventName.trim() ? eventName : LL.event.event(),
                eventLocation && eventLocation.trim() ? eventLocation : LL.event.unknownLocation(),
                `${date} ${eventStartTime ?? '00:00:00'}`,
                `${date} ${eventEndTime ?? '23:59:59'}`
            )
            .download(`${date}_${category.name.replace(/\s+/g, '_')}.ics`);
    };

    const handleSave = async () => {
        try {
            await addPlanResource({ date, resource: selectedUser, category: category.id }).unwrap();
            // Record the timestamp so the 5-minute self-delete window can be checked later
            try {
                const key = `plan_added_${date}_${category.id}_${selectedUser}`;
                localStorage.setItem(key, Date.now().toString());
            } catch {
                /* ignore */
            }
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleRequestChange = async () => {
        try {
            if (!effectiveAssignedUser) {
                return;
            }
            await createChangeRequest({
                date,
                category: category.id,
                resource: effectiveAssignedUser.id,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleCancelRequest = async () => {
        try {
            if (!effectiveAssignedUser) {
                return;
            }
            await cancelChangeRequest({
                date,
                category: category.id,
                resource: effectiveAssignedUser.id,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleAcceptRequest = async () => {
        try {
            if (!effectiveAssignedUser) {
                return;
            }
            await acceptChangeRequest({
                date,
                category: category.id,
                acceptingResourceId: userInfo.userId,
                requestingResource: effectiveAssignedUser.id,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleNotifyChange = async () => {
        try {
            if (!effectiveAssignedUser) {
                return;
            }

            await notifyChange({
                issuerId: effectiveAssignedUser.id,
                issuerName: effectiveAssignedUser.name,
                date,
                categoryId: category.id,
                categoryName: category.name,
            }).unwrap();

            setNotified(true);
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleDelete = async () => {
        try {
            if (!effectiveAssignedUser) return;
            await cancelChangeRequest({ date, category: category.id }).unwrap();
            await deletePlanResource({
                date,
                category: category.id,
                resource: effectiveAssignedUser.id,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    /** Delete handler for regular (non-admin) users — respects the 5-minute window. */
    const handleSelfDelete = () => {
        if (!effectiveAssignedUser) return;
        const key = `plan_added_${date}_${category.id}_${effectiveAssignedUser.id}`;
        let withinWindow = false;
        try {
            const stored = localStorage.getItem(key);
            if (stored) {
                const ts = parseInt(stored, 10);
                withinWindow = Date.now() - ts < 5 * 60 * 1000;
            }
        } catch {
            /* ignore */
        }

        if (withinWindow) {
            void handleDelete();
        } else {
            setDeleteExpiredOpen(true);
        }
    };

    const handleAddToActiveFilters = async () => {
        try {
            if (!resourcesSettings) return;
            if (resourcesSettings.hiddenCategories.includes(category.name)) return;

            await saveResourcesSettings({
                hiddenCategories: [...resourcesSettings.hiddenCategories, category.name],
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleShowAudit = () => {
        setAuditOpen(true);
    };

    if (effectiveAssignedUser) {
        const user = effectiveAssignedUser;

        const isHighlighted = assigned?.requestedUsers?.includes(user.id) ?? false;
        const isSelf = isMyAssignment;

        const variant = isHighlighted ? 'highlighted' : isSelf ? 'self' : 'default';
        const iconBgColor = isHighlighted
            ? 'warning.dark'
            : isSelf
              ? 'primary.main'
              : 'primary.dark';

        const showButtons =
            canInteract ||
            (!canInteract && isHighlighted) ||
            userInfo.admin ||
            userInfo.categoriesResponsible?.includes(category.id);

        return (
            <Stack sx={{ position: 'relative' }}>
                <EventAuditModal
                    open={auditOpen}
                    onClose={() => setAuditOpen(false)}
                    date={date}
                    categoryId={category.id}
                    categoryName={category.name}
                />
                <DeleteWindowExpiredDialog
                    open={deleteExpiredOpen}
                    onClose={() => setDeleteExpiredOpen(false)}
                    onRequestReplacement={handleRequestChange}
                />
                {isLoading && (
                    <Stack
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'action.hover',
                            zIndex: 10,
                            backdropFilter: 'blur(2px)',
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={20} />
                    </Stack>
                )}
                <ContainerBox variant={variant} gap={3}>
                    <CategoryIconBox category={category} bgcolor={iconBgColor} />

                    <Stack sx={{ flex: 1, minHeight: 48, minWidth: 0, justifyContent: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {category.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isSelf ? 700 : 600,
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                            }}
                        >
                            {user.name}
                        </Typography>
                    </Stack>

                    {showButtons && (
                        <ButtonGroup size="small" variant="text" sx={{ flexShrink: 0 }}>
                            {canInteract && isHighlighted && (
                                <>
                                    <ActionButton
                                        title={LL.categoryItem.cancelRequest()}
                                        icon={<UndoIcon fontSize="inherit" />}
                                        onClick={handleCancelRequest}
                                        isLoading={isCancellingRequest}
                                    />
                                    {!notified && (
                                        <ActionButton
                                            title={LL.categoryItem.notify()}
                                            icon={<EmailIcon fontSize="inherit" />}
                                            onClick={handleNotifyChange}
                                            isLoading={isNotifying}
                                        />
                                    )}
                                </>
                            )}
                            {canInteract && !isHighlighted && (
                                <>
                                    <ActionButton
                                        title={LL.categoryItem.requestReplacement()}
                                        icon={<ChangeIcon fontSize="inherit" />}
                                        onClick={handleRequestChange}
                                        isLoading={isCreatingRequest}
                                    />
                                    <ActionButton
                                        title={LL.categoryItem.addToCalendar()}
                                        icon={<CalendarIcon fontSize="inherit" />}
                                        onClick={handleAddToCalendar}
                                    />
                                </>
                            )}

                            {!canInteract && isHighlighted && (
                                <ActionButton
                                    title={LL.categoryItem.substitute()}
                                    icon={<ReplacementIcon fontSize="inherit" />}
                                    onClick={handleAcceptRequest}
                                    color="success.main"
                                    isLoading={isAcceptingRequest}
                                />
                            )}

                            <IconButton
                                size="small"
                                onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
                            >
                                <MoreVertIcon fontSize="inherit" />
                            </IconButton>
                            <Menu
                                anchorEl={adminMenuAnchor}
                                open={Boolean(adminMenuAnchor)}
                                onClose={() => setAdminMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {/* ── Own-assignment actions (canInteract) ── */}
                                {canInteract && !isHighlighted && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleRequestChange();
                                            setAdminMenuAnchor(null);
                                        }}
                                        disabled={isCreatingRequest}
                                    >
                                        <ListItemIcon>
                                            <ChangeIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {LL.categoryItem.requestReplacement()}
                                        </ListItemText>
                                    </MenuItem>
                                )}

                                {canInteract && isHighlighted && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleCancelRequest();
                                            setAdminMenuAnchor(null);
                                        }}
                                        disabled={isCancellingRequest}
                                    >
                                        <ListItemIcon>
                                            <UndoIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {LL.categoryItem.cancelRequest()}
                                        </ListItemText>
                                    </MenuItem>
                                )}

                                {canInteract && isHighlighted && !notified && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleNotifyChange();
                                            setAdminMenuAnchor(null);
                                        }}
                                        disabled={isNotifying}
                                    >
                                        <ListItemIcon>
                                            <EmailIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>{LL.categoryItem.notify()}</ListItemText>
                                    </MenuItem>
                                )}

                                {canInteract && (
                                    <MenuItem
                                        onClick={() => {
                                            handleAddToCalendar();
                                            setAdminMenuAnchor(null);
                                        }}
                                    >
                                        <ListItemIcon>
                                            <CalendarIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>
                                            {LL.categoryItem.addToCalendar()}
                                        </ListItemText>
                                    </MenuItem>
                                )}

                                {/* ── Substitute (non-self, highlighted slot) ── */}
                                {!canInteract && isHighlighted && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleAcceptRequest();
                                            setAdminMenuAnchor(null);
                                        }}
                                        disabled={isAcceptingRequest}
                                    >
                                        <ListItemIcon>
                                            <ReplacementIcon
                                                fontSize="small"
                                                sx={{ color: 'success.main' }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText>{LL.categoryItem.substitute()}</ListItemText>
                                    </MenuItem>
                                )}

                                {/* ── View / filter ── only useful when the user has no involvement with the category */}
                                {!canEdit && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleAddToActiveFilters();
                                            setAdminMenuAnchor(null);
                                        }}
                                        disabled={isSavingFilter}
                                    >
                                        <ListItemIcon>
                                            <FilterIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>{LL.categoryItem.addToFilter()}</ListItemText>
                                    </MenuItem>
                                )}

                                {/* ── Divider before destructive / admin actions ── */}
                                {(showAuditButton ||
                                    userInfo.admin ||
                                    userInfo.categoriesResponsible?.includes(category.id) ||
                                    canInteract) && <Divider />}

                                {/* ── Admin / audit actions ── */}
                                {showAuditButton && (
                                    <MenuItem
                                        onClick={() => {
                                            handleShowAudit();
                                            setAdminMenuAnchor(null);
                                        }}
                                    >
                                        <ListItemIcon>
                                            <HistoryIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>{LL.categoryItem.history()}</ListItemText>
                                    </MenuItem>
                                )}

                                {(userInfo.admin ||
                                    userInfo.categoriesResponsible?.includes(category.id)) && (
                                    <MenuItem
                                        onClick={() => {
                                            void handleDelete();
                                            setAdminMenuAnchor(null);
                                        }}
                                    >
                                        <ListItemIcon>
                                            <CloseIcon
                                                fontSize="small"
                                                sx={{ color: 'error.main' }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText>{LL.common.delete()}</ListItemText>
                                    </MenuItem>
                                )}

                                {/* Self-delete for non-admin, non-responsible assigned users (5-min window) */}
                                {canInteract &&
                                    !userInfo.admin &&
                                    !userInfo.categoriesResponsible?.includes(category.id) && (
                                        <MenuItem
                                            onClick={() => {
                                                handleSelfDelete();
                                                setAdminMenuAnchor(null);
                                            }}
                                        >
                                            <ListItemIcon>
                                                <CloseIcon
                                                    fontSize="small"
                                                    sx={{ color: 'error.main' }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText>{LL.common.delete()}</ListItemText>
                                        </MenuItem>
                                    )}
                            </Menu>
                        </ButtonGroup>
                    )}
                </ContainerBox>
            </Stack>
        );
    }

    if (canEdit) {
        // Ensure the selected value exists in the MenuItem options
        const validatedValue = category.resources.some((r) => r.id === selectedUser)
            ? selectedUser
            : (category.resources[0]?.id ?? '');

        // Check if the currently selected resource is a system assistant (save should be blocked)
        const selectedResource = category.resources.find((r) => r.id === validatedValue);
        const isSystemAssistantSelected = !!(
            selectedResource?.system && selectedResource?.assistant
        );

        return (
            <Box sx={{ position: 'relative' }}>
                <EventAuditModal
                    open={auditOpen}
                    onClose={() => setAuditOpen(false)}
                    date={date}
                    categoryId={category.id}
                    categoryName={category.name}
                />
                {isLoading && (
                    <Stack
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'action.hover',
                            zIndex: 10,
                            backdropFilter: 'blur(2px)',
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={20} />
                    </Stack>
                )}
                <ContainerBox variant="edit" gap={3}>
                    <CategoryIconBox category={category} bgcolor="action.selected" />

                    <Stack sx={{ flex: 1, minHeight: 48, minWidth: 0, justifyContent: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {category.name}
                        </Typography>
                        <FormControl fullWidth sx={{ flex: 1 }}>
                            <Select
                                value={validatedValue}
                                onChange={(e) => setSelectedUser(e.target.value as number)}
                                sx={{
                                    fontSize: '0.875rem',
                                    '& .MuiSelect-select': {
                                        py: 0.4,
                                    },
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                {category.resources.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        {LL.categoryItem.noResourcesAvailable()}
                                    </MenuItem>
                                ) : (
                                    [...category.resources]
                                        .sort((a, b) => {
                                            // Primary order: system users (0), members (1), assistants (2)
                                            const rank = (r: typeof a) =>
                                                r.system ? 0 : r.assistant ? 2 : 1;
                                            const rankDiff = rank(a) - rank(b);
                                            if (rankDiff !== 0) return rankDiff;
                                            // Secondary order: alphabetical by name
                                            return (a.name ?? '').localeCompare(b.name ?? '');
                                        })
                                        .map((resource) => {
                                            const isAbsent = absent.hasOwnProperty(resource.id);
                                            const isAdmin = userInfo.admin;
                                            const isCategoryResponsible =
                                                userInfo.categoriesResponsible?.includes(
                                                    category.id
                                                );
                                            const isCurrentUser = resource.id === userInfo.userId;
                                            const isProxyUser = userInfo.represents?.includes(
                                                resource.id
                                            );

                                            const isAlreadyAssigned = alreadyAssignedUserIds.has(
                                                resource.id
                                            );

                                            // Determine if this menu item should be disabled
                                            // System assistants are always selectable (save will be blocked instead)
                                            const isSystemAssistant = !!(
                                                resource.system && resource.assistant
                                            );
                                            const isDisabled =
                                                !isSystemAssistant &&
                                                // If user is not admin and not category responsible
                                                (!isAdmin && !isCategoryResponsible
                                                    ? !isCurrentUser && !isProxyUser // Can only select self or proxies
                                                    : isAbsent && !isCurrentUser); // Admin/responsible can select anyone not absent (except themselves even if absent)

                                            return (
                                                <MenuItem
                                                    key={resource.id}
                                                    value={resource.id}
                                                    disabled={isDisabled || isAlreadyAssigned}
                                                    sx={{ fontSize: '0.875rem' }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{ alignItems: 'center' }}
                                                        title={
                                                            resource.assistant
                                                                ? LL.userCard.assistant()
                                                                : LL.userCard.member()
                                                        }
                                                    >
                                                        {resource.system ? (
                                                            resource.name
                                                        ) : absent.hasOwnProperty(resource.id) ? (
                                                            <>
                                                                <FlightIcon
                                                                    fontSize="inherit"
                                                                    color="warning"
                                                                />
                                                                ({resource.name})
                                                            </>
                                                        ) : resource.assistant ? (
                                                            <>
                                                                <AssistantIcon
                                                                    fontSize="inherit"
                                                                    color="secondary"
                                                                />
                                                                ({resource.name})
                                                            </>
                                                        ) : (
                                                            <>
                                                                <MemberIcon
                                                                    fontSize="inherit"
                                                                    color="primary"
                                                                />
                                                                {resource.name}
                                                            </>
                                                        )}
                                                    </Stack>
                                                </MenuItem>
                                            );
                                        })
                                )}
                            </Select>
                        </FormControl>
                    </Stack>

                    <ButtonGroup size="small" variant="text">
                        <ActionButton
                            title={
                                isSystemAssistantSelected
                                    ? LL.categoryItem.systemAssistantSaveDisabled()
                                    : LL.common.save()
                            }
                            icon={<SaveIcon fontSize="inherit" />}
                            onClick={handleSave}
                            color="primary.main"
                            disabled={category.resources.length === 0 || isSystemAssistantSelected}
                            isLoading={isAdding}
                        />
                        {showAdminMenu && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
                                >
                                    <MoreVertIcon fontSize="inherit" />
                                </IconButton>
                                <Menu
                                    anchorEl={adminMenuAnchor}
                                    open={Boolean(adminMenuAnchor)}
                                    onClose={() => setAdminMenuAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    {showAuditButton && (
                                        <MenuItem
                                            onClick={() => {
                                                handleShowAudit();
                                                setAdminMenuAnchor(null);
                                            }}
                                        >
                                            <ListItemIcon>
                                                <HistoryIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{LL.categoryItem.history()}</ListItemText>
                                        </MenuItem>
                                    )}
                                </Menu>
                            </>
                        )}
                    </ButtonGroup>
                </ContainerBox>
            </Box>
        );
    }

    // Show a special message if user is absent and would normally be able to edit
    if (wouldBeAbleToEditIfNotAbsent) {
        return (
            <Tooltip title={LL.categoryItem.absentWarning()} arrow>
                <ContainerBox variant="empty" gap={3}>
                    <CategoryIconBox
                        category={category}
                        bgcolor="warning.dark"
                        textColor="warning.light"
                    />

                    <Stack sx={{ flex: 1, minHeight: 48, minWidth: 0, justifyContent: 'center' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {category.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'warning.light',
                                fontSize: '0.875rem',
                                fontStyle: 'italic',
                            }}
                        >
                            {LL.categoryItem.absent()}
                        </Typography>
                    </Stack>
                </ContainerBox>
            </Tooltip>
        );
    }

    return (
        <ContainerBox variant="empty" gap={3}>
            <CategoryIconBox
                category={category}
                bgcolor="action.disabledBackground"
                textColor="text.secondary"
            />
            <Stack sx={{ flex: 1, minHeight: 48, minWidth: 0, justifyContent: 'center' }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                    }}
                >
                    {category.name}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                    }}
                >
                    ?
                </Typography>
            </Stack>
            <ActionButton
                title={LL.categoryItem.addToFilter()}
                icon={<FilterIcon fontSize="inherit" />}
                onClick={handleAddToActiveFilters}
                isLoading={isSavingFilter}
            />
        </ContainerBox>
    );
};

export default CategoryItem;
